// Phase 4 — Public registration form.
//
// Anyone can hit this screen from the login page. It creates a Pending
// user server-side; an admin approves from Admin → Pending Registrations
// and only then does the applicant get login access + team memberships.
//
// UX notes:
//   • Date of Joining is set by the backend on approval, but we display
//     today's date here as a courtesy so the applicant knows what will
//     go on their record.
//   • Team picker uses the same fancy chip UI as the Splits view.
//   • Success screen replaces the form once submitted so users don't
//     accidentally double-submit.

import React, { useEffect, useMemo, useState } from "react";
import {
  User as UserIcon, Mail, Phone, Calendar, Lock, Eye, EyeOff,
  ArrowLeft, Check, Trophy, Volleyball, CheckCircle2
} from "lucide-react";
import { api } from "../api";

const EMPTY = {
  firstName: "", lastName: "",
  dateOfBirth: "",
  email: "", phone: "",
  password: "", confirmPassword: "",
  teamIds: []
};

function todayIso() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export default function Register({ onBack }) {
  const [form, setForm] = useState(EMPTY);
  const [teams, setTeams] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const today = useMemo(todayIso, []);

  useEffect(() => {
    api.listPublicTeams()
      .then(setTeams)
      .catch(() => setTeams([]));
  }, []);

  function update(field, value) {
    setForm(f => ({ ...f, [field]: value }));
    setError("");
  }
  function toggleTeam(id) {
    setForm(f => ({
      ...f,
      teamIds: f.teamIds.includes(id)
        ? f.teamIds.filter(x => x !== id)
        : [...f.teamIds, id]
    }));
  }

  async function submit(e) {
    e.preventDefault();
    setError("");

    if (!form.firstName.trim())   return setError("First name is required.");
    if (!form.lastName.trim())    return setError("Last name is required.");
    if (!form.dateOfBirth)        return setError("Date of birth is required.");
    if (!form.email.trim())       return setError("Email is required.");
    if (!form.phone.trim())       return setError("Phone number is required.");
    if (!form.password)           return setError("Pick a password.");
    if (form.password.length < 6) return setError("Password must be at least 6 characters.");
    if (form.password !== form.confirmPassword) return setError("Passwords don't match.");
    if (form.teamIds.length === 0) return setError("Pick at least one team you're interested in.");

    setBusy(true);
    try {
      await api.register({
        firstName:   form.firstName.trim(),
        lastName:    form.lastName.trim(),
        dateOfBirth: form.dateOfBirth,
        email:       form.email.trim(),
        phone:       form.phone.trim(),
        password:    form.password,
        teamIds:     form.teamIds
      });
      setSubmitted(true);
    } catch (err) {
      setError(err.message || "Registration failed. Try again.");
    } finally { setBusy(false); }
  }

  // ── SUCCESS SCREEN ─────────────────────────────────────────────
  if (submitted) {
    return (
      <div className="register-page">
        <div className="register-card register-card--success">
          <div className="register-success-iconWrap">
            <CheckCircle2 size={44}/>
          </div>
          <h2>You're on the list.</h2>
          <p>
            Thanks for applying to Gabruz Universe. An admin will review your
            registration and you'll receive an email once you're approved.
          </p>
          <button type="button" className="register-btn register-btn--ghost" onClick={onBack}>
            <ArrowLeft size={14}/> Back to sign in
          </button>
        </div>
      </div>
    );
  }

  // ── FORM ───────────────────────────────────────────────────────
  return (
    <div className="register-page">
      <button type="button" className="register-back" onClick={onBack}>
        <ArrowLeft size={16}/> Back to sign in
      </button>

      <div className="register-card">
        <header className="register-head">
          <h1>Apply to join</h1>
          <small>Registration is reviewed by a club admin. You'll be able to sign in once you're approved.</small>
        </header>

        <form className="register-form" onSubmit={submit}>
          {/* Names */}
          <div className="register-row">
            <label className="register-field">
              <span>First name *</span>
              <div className="register-input">
                <UserIcon size={14}/>
                <input value={form.firstName} onChange={e => update("firstName", e.target.value)} placeholder="Ashish" autoComplete="given-name"/>
              </div>
            </label>
            <label className="register-field">
              <span>Last name *</span>
              <div className="register-input">
                <UserIcon size={14}/>
                <input value={form.lastName} onChange={e => update("lastName", e.target.value)} placeholder="Bhat" autoComplete="family-name"/>
              </div>
            </label>
          </div>

          {/* DOB + Date of Joining */}
          <div className="register-row">
            <label className="register-field">
              <span>Date of birth *</span>
              <div className="register-input">
                <Calendar size={14}/>
                <input type="date" value={form.dateOfBirth} onChange={e => update("dateOfBirth", e.target.value)} max={today}/>
              </div>
            </label>
            <label className="register-field">
              <span>Date of joining</span>
              <div className="register-input register-input--disabled">
                <Calendar size={14}/>
                <input type="date" value={today} disabled/>
              </div>
              <em className="register-hint">Auto-filled to today's date.</em>
            </label>
          </div>

          {/* Email + Phone */}
          <div className="register-row">
            <label className="register-field">
              <span>Email *</span>
              <div className="register-input">
                <Mail size={14}/>
                <input type="email" value={form.email} onChange={e => update("email", e.target.value)} placeholder="you@example.com" autoComplete="email"/>
              </div>
            </label>
            <label className="register-field">
              <span>Phone number *</span>
              <div className="register-input">
                <Phone size={14}/>
                <input type="tel" value={form.phone} onChange={e => update("phone", e.target.value)} placeholder="412-555-0000" autoComplete="tel"/>
              </div>
            </label>
          </div>

          {/* Password + Confirm */}
          <div className="register-row">
            <label className="register-field">
              <span>Choose a password *</span>
              <div className="register-input">
                <Lock size={14}/>
                <input
                  type={showPassword ? "text" : "password"}
                  value={form.password}
                  onChange={e => update("password", e.target.value)}
                  placeholder="At least 6 characters"
                  autoComplete="new-password"
                />
                <button type="button" className="register-eye" onClick={() => setShowPassword(p => !p)} tabIndex={-1} aria-label={showPassword ? "Hide password" : "Show password"}>
                  {showPassword ? <EyeOff size={14}/> : <Eye size={14}/>}
                </button>
              </div>
            </label>
            <label className="register-field">
              <span>Confirm password *</span>
              <div className="register-input">
                <Lock size={14}/>
                <input
                  type={showPassword ? "text" : "password"}
                  value={form.confirmPassword}
                  onChange={e => update("confirmPassword", e.target.value)}
                  placeholder="Re-enter"
                  autoComplete="new-password"
                />
              </div>
            </label>
          </div>

          {/* Team interests */}
          <div className="register-field">
            <div className="register-sectionLabel">
              <span>Interested in joining *</span>
              <em>{form.teamIds.length} selected</em>
            </div>
            <div className="register-teamPicker">
              {teams.length === 0 && (
                <div className="register-teamPicker-empty">Loading teams…</div>
              )}
              {teams.map(t => {
                const on = form.teamIds.includes(t.id);
                const isCricket = t.sport === "Cricket";
                return (
                  <button
                    key={t.id}
                    type="button"
                    className={`register-teamChip register-teamChip--${isCricket ? "cricket" : "volleyball"}${on ? " register-teamChip--on" : ""}`}
                    onClick={() => toggleTeam(t.id)}
                  >
                    <span className="register-teamChip-iconWrap">
                      {isCricket ? <Trophy size={14}/> : <Volleyball size={14}/>}
                    </span>
                    <span className="register-teamChip-body">
                      <strong>{t.name}</strong>
                      <small>{t.sport}</small>
                    </span>
                    {on && <Check size={14} strokeWidth={3}/>}
                  </button>
                );
              })}
            </div>
          </div>

          {error && <div className="register-err">{error}</div>}

          <div className="register-actions">
            <button type="button" className="register-btn register-btn--ghost" onClick={onBack} disabled={busy}>Cancel</button>
            <button type="submit" className="register-btn register-btn--primary" disabled={busy}>
              {busy ? "Submitting…" : "Submit application"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
